import { listOf, splitFlag } from "@volca/ui/trades";
import type { RecordObject } from "@/profitability/records/objects";
import type { ReviewSpec } from "@/profitability/review/review-spec";

/** The chat's words when nothing is known about the report yet. */
export const CHAT_SURFACE = {
  title: "Job Costing chat",
  subtitle: "Chats about your job costing reports",
  emptyTitle: "Ask about this report",
  emptyDescription: "Find out what drove this report's figures.",
  composerPlaceholder: "Ask about this report, or any other period…",
  suggestedPrompts: [
    "Summarize this report",
    "What stands out in this report?",
    "How does this compare to the period before?",
  ],
};

export type ChatSurface = typeof CHAT_SURFACE;

const MAX_PROMPTS = 3;

/**
 * The chat's words from what the report declares rather than from what a
 * job costing report usually holds: its records' plurals ("jobs or
 * technicians"), its headline figures ("revenue and gross profit"), the
 * name of its rate and its flag rules. Whatever the report does not
 * declare falls back to the generic words above.
 */
export const chatSurface = ({
  objects = [],
  figures = [],
  spec,
}: {
  objects?: RecordObject[];
  /** The headline figures' labels, in the order the report leads with them. */
  figures?: string[];
  spec?: ReviewSpec | null;
}): ChatSurface => {
  const plurals = objects.map((object) => object.plural.toLowerCase());
  // The report's own labels, as written: lowercasing breaks "GM %".
  const named = figures.slice(0, 2);
  const work = objects.find((object) => object.dataset.id === spec?.work);
  // A rollup is what people rank (a job's technician); the work itself
  // when the report has no other records.
  const ranked = objects.find((object) => object !== work) ?? work;
  const rated =
    ranked &&
    ["total", "result"].every((role) =>
      ranked.dataset.columns.some((column) => column.role === role),
    );
  const measure = rated && spec ? spec.metric.label : figures[0];
  const flag = work?.dataset.columns.find((column) => column.flag);
  const rule = flag
    ? work?.dataset.rows
        .map((row) => row[flag.key])
        .find((value) => typeof value === "string" && value.trim())
    : undefined;
  const prompts = [
    "Summarize this report",
    ranked && measure
      ? `Which ${ranked.plural.toLowerCase()} had the lowest ${measure}?`
      : null,
    typeof rule === "string"
      ? `Why were ${(work?.plural ?? "records").toLowerCase()} flagged for “${splitFlag(rule).rule}”?`
      : null,
    "How does this compare to the period before?",
  ].filter((prompt): prompt is string => !!prompt);
  return {
    ...CHAT_SURFACE,
    emptyDescription: named.length
      ? `Find out what drove ${listOf(named)}${plurals.length ? `, by ${plurals.slice(0, 2).join(" or ")}` : ""}.`
      : CHAT_SURFACE.emptyDescription,
    composerPlaceholder: plurals.length
      ? `Ask about these ${plurals[0]}, or any other period…`
      : CHAT_SURFACE.composerPlaceholder,
    suggestedPrompts: prompts.slice(0, MAX_PROMPTS),
  };
};
